import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ImageUpload } from "@/components/ImageUpload";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, CalendarDays, MapPin } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", date: "", time: "", location: "", description: "", image_path: null, published: false };

export default function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/events").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openAdd = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ name: ev.name, date: ev.date, time: ev.time || "", location: ev.location || "", description: ev.description || "", image_path: ev.image_path || null, published: ev.published }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/events/${editing.id}`, form);
      else await api.post("/admin/events", form);
      toast.success(editing ? "Event updated" : "Event created");
      setDialogOpen(false);
      load();
    } catch { toast.error("Failed to save event"); } finally { setSaving(false); }
  };

  const togglePublish = async (ev) => {
    try {
      await api.patch(`/admin/events/${ev.id}/publish`, { published: !ev.published });
      toast.success(!ev.published ? "Published to public site" : "Unpublished");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/admin/events/${deleteId}`); toast.success("Event deleted"); load(); } catch { toast.error("Delete failed"); }
    setDeleteId(null);
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">Events</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Manage Events</h1>
        </div>
        <Button onClick={openAdd} className="bg-[#194324] hover:bg-[#2D623A] text-white rounded-full" data-testid="admin-add-event-button">
          <Plus className="h-4 w-4 mr-2" /> Add Event
        </Button>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2DAD0] p-12 text-center text-[#5C7264]"><CalendarDays className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />No events yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((ev) => (
            <div key={ev.id} className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden" data-testid={`event-card-${ev.id}`}>
              <div className="h-40 bg-[#F2EEE5] relative">
                {ev.image_path ? <img src={fileUrl(ev.image_path)} alt={ev.name} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><CalendarDays className="h-10 w-10 text-[#CBD2CC]" /></div>}
                <Badge className={`absolute top-3 left-3 border-0 ${ev.published ? "bg-[#194324] text-white" : "bg-[#F9F3E3] text-[#A8811A]"}`}>{ev.published ? "Published" : "Draft"}</Badge>
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-semibold text-[#194324] leading-tight">{ev.name}</h3>
                <div className="mt-2 text-sm text-[#5C7264] space-y-1">
                  <div className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" />{ev.date} {ev.time && `· ${ev.time}`}</div>
                  {ev.location && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{ev.location}</div>}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[#F2EEE5] pt-4">
                  <div className="flex items-center gap-2">
                    <Switch checked={ev.published} onCheckedChange={() => togglePublish(ev)} data-testid={`event-publish-toggle-switch-${ev.id}`} />
                    <span className="text-xs text-[#5C7264]">{ev.published ? "Public" : "Hidden"}</span>
                  </div>
                  <div>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}><Pencil className="h-4 w-4 text-[#2D623A]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteId(ev.id)} data-testid={`event-delete-${ev.id}`}><Trash2 className="h-4 w-4 text-[#D9531E]" /></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{editing ? "Edit Event" : "Add Event"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Event Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="event-form-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="event-form-date" /></div>
              <div className="space-y-2"><Label>Time</Label><Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="e.g. 6:00 AM" data-testid="event-form-time" /></div>
            </div>
            <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Gandhidham, Kutch" data-testid="event-form-location" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="event-form-description" /></div>
            <div className="space-y-2"><Label>Image</Label><ImageUpload value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} testid="event-form-image" /></div>
            <div className="flex items-center gap-3 bg-[#F9F3E3] rounded-lg px-4 py-3">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="event-form-publish" />
              <span className="text-sm text-[#194324]">Publish to public site</span>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid="event-form-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Create Event"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete event?</AlertDialogTitle><AlertDialogDescription>This will remove it everywhere.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-[#D9531E] hover:bg-[#b8441a]" data-testid="confirm-delete-button">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
